import { get } from 'lodash';

import * as types from './types';
import { createToast, toastType } from '../../../common/ToastHelper';
import { resetUploadProgress } from '../../../common/UI/state/actions';
import * as ImportSettingHelpers from '../ImportSettingsHelper';
import { mealsDataMapped } from '../fixtures';
import * as ImportStudentSelectors from './selectors';

/**
 * Mapping the redux dispatch function to prop
 *
 * @param {function} dispatch
 */
export const dispatch = (dispatch) => {
  return dispatch;
};

export const postImportStudentsFile = (formData) => {
  return (dispatch, getState, MPWWebApi) => {
    dispatch(importStudentNotReady());

    MPWWebApi.importStudentsApi
      .postImportStudentsFileData(formData, dispatch)
      .then((response) => {
        dispatch(ingestImportData(response));
        dispatch(resetUploadProgress());
        dispatch(importStudentReady());
      })
      .catch((error) => {
        dispatch(importDataError(error));
        dispatch(resetUploadProgress());
        dispatch(importStudentReady());
      });
  };
};

export const getLanguagesAction = () => (dispatch, getState, MPWWebApi) =>
  MPWWebApi.importStudentsApi
    .getLanguages()
    .then((response) => {
      dispatch({
        type: types.GET_LANGUAGES,
        payload: response,
      });
    })
    .catch((error) => {
      dispatch({
        type: types.GET_LANGUAGES_ERROR,
        payload: error.response,
      });
    });

export const postImportStudentsAction = (obj) => {
  return async (dispatch, getState, MPWWebApi) => {
    try {
      dispatch(importStudentNotReady());
      const response = await MPWWebApi.importStudentsApi.postImportStudents(
        obj
      );

      dispatch({
        type: types.POST_IMPORTSTUDENTS,
        payload: response,
      });
      dispatch({
        type: types.IMPORTSTUDENT_GET_CONFIG,
        payload: { data: { Data: obj.ImportStudentHeader } },
      });
      if (get(obj, 'ImportSchedule', null)) {
        dispatch({
          type: types.POST_IMPORT_SCHEDULE,
          payload: { data: { Data: obj.ImportSchedule } },
        });
      }

      createToast(
        toastType.success,
        'Success',
        response.data.Data.Message,
        true
      );
    } catch (error) {
      dispatch({
        type: types.POST_IMPORTSTUDENTS_ERROR,
        payload: error.response,
      });
      if (error.response.data.Errors[0].Title === null) {
        createToast(
          toastType.error,
          'Error',
          'Please map fields correctly',
          true
        );
      } else {
        createToast(
          toastType.error,
          error.response.data.Errors[0].Title,
          error.response.data.Errors[0].Message,
          true
        );
      }
    } finally {
      dispatch(importStudentReady());
    }
  };
};

export const importAction = (obj) => {
  return async (dispatch, getState, MPWWebApi) => {
    try {
      const response = await MPWWebApi.importStudentsApi.importStudents(
        obj,
        dispatch
      );
      dispatch({
        type: types.IMPORT_STUDENT,
        payload: response,
      });
      createToast(toastType.success, 'Success', response.data.Data, true);
    } catch (error) {
      dispatch({
        type: types.IMPORT_STUDENT_ERROR,
        payload: error.response,
      });
      if (error.response.data.Errors[0].Title === null) {
        createToast(
          toastType.error,
          'Error',
          'Please map fields correctly',
          true
        );
      } else {
        createToast(
          toastType.error,
          error.response.data.Errors[0].Title,
          error.response.data.Errors[0].Message,
          true
        );
      }
    }
  };
};

export const addLanguageMappingDataAction = (data) => {
  return {
    type: types.ADD_LANGUAGE_MAPPING_DATA,
    payload: data,
  };
};

export const setSiteMappingDataAction = (data) => {
  return {
    type: types.SET_SITE_MAPPING_DATA,
    payload: data,
  };
};

export const setGradeMappingDataAction = (data) => {
  return {
    type: types.SET_GRADE_MAPPING_DATA,
    payload: data,
  };
};

export const addingMealMappingDataAction = (data) => {
  return {
    type: types.ADD_MEAL_MAPPING_DATA,
    payload: data,
  };
};

export const clearMappingDataAction = () => {
  return {
    type: types.CLEAR_MAPPING_DATA,
  };
};

export const clearAddedMappingDataAction = () => {
  return {
    type: types.CLEAR_ADDED_MAPPING_DATA,
  };
};

export const clearStudentHeaderAction = () => {
  return {
    type: types.IMPORTSTUDENT_HEADER_ERROR,
  };
};

export const clearImportStudentsErrors = () => {
  return {
    type: types.IMPORTSTUDENT_CLEAR_ERROR,
  };
};

export const clearPostImportStudentsAction = () => {
  return {
    type: types.POST_IMPORTSTUDENTS_CLEAR_DATA,
  };
};

export const clearImportStudentsData = () => {
  return {
    type: types.IMPORTSTUDENT_CLEAR_DATA,
  };
};

export const setImportStudentsErrors = (message) => {
  return {
    type: types.IMPORTSTUDENT_ERROR,
    payload: {
      data: {
        Data: [],
        Errors: [{ Title: '', Message: `${message}`, Scenario: null }],
      },
    },
  };
};

export const setImportStudentsConfigErrors = (message) => {
  return {
    type: types.IMPORTSTUDENT_CONFIG_ERROR,
    payload: {
      data: {
        Errors: [{ Title: '', Message: `${message}`, Scenario: null }],
      },
    },
  };
};

export const setImportStudentReady = (ready) => ({
  type: types.IMPORT_STUDENT_READY,
  payload: ready,
});

export const importStudentReady = () => setImportStudentReady(true);

export const initializeMappingData = () => (dispatch, getState) => {
  const state = getState();
  const finalMappedList = ImportStudentSelectors.getMappedLanguageList(state);
  const storedMealMappingData = ImportStudentSelectors.getStoredMealMappingData(
    state
  );

  dispatch(addLanguageMappingDataAction(finalMappedList));
  dispatch(
    addingMealMappingDataAction(
      ImportSettingHelpers.formatMealData(
        storedMealMappingData,
        mealsDataMapped
      )
    )
  );
};

export const importStudentNotReady = () => setImportStudentReady(false);

export const addColumnMapping = (columnMapping) => ({
  type: types.ADD_COLUMN_MAPPING,
  payload: columnMapping,
});

export const removeFromColumnMap = (id) => ({
  type: types.REMOVE_COLUMN_MAPPING,
  payload: id,
});

export const ingestImportData = (payload) => ({
  type: types.IMPORTSTUDENT_IMPORT,
  payload: payload,
});

export const importDataError = (error) => ({
  type: types.IMPORTSTUDENT_ERROR,
  payload: error.response,
});

export const deleteAutoImport = (id) => {
  return async (dispatch, getState, MPWWebApi) => {
    try {
      await MPWWebApi.importStudentsApi.deleteImportSettings(id, dispatch);

      dispatch({
        type: types.REMOVE_IMPORTSTUDENT_SETTINGS,
        payload: true,
      });
      dispatch(getImportSettings());
    } catch (error) {
      dispatch({
        type: types.IMPORTSTUDENT_SETTINGS_ERROR,
        payload: error.response.data.Errors[0].Message,
      });
    }
  };
};

export const getImportSettings = () => {
  return async (dispatch, getState, MPWWebApi) => {
    try {
      MPWWebApi.importStudentsApi.getImportSettings().then((response) => {
        dispatch({
          type: types.GET_IMPORTSTUDENT_SETTINGS,
          payload: response,
        });
      });
    } catch (error) {
      dispatch({
        type: types.IMPORTSTUDENT_SETTINGS_ERROR,
        payload: error.response.data.Errors[0].Message,
      });
    }
  };
};



// WEBPACK FOOTER //
// ./src/pages/importstudents/state/actions.js